import {useContext, useEffect, useState} from 'react'
import { useNavigate } from 'react-router-dom'
import utility from 'src/utils/utility'
import {
    Card, Rectangle, Avatar, Badge, Button, ChangePasswordModal,
    ChangePasswordValue, Text, Flex, Box, tokens,
} from 'src/componentv2'
import { fontLabel, fontBold } from 'src/model/v2/interface/constants'
import { useSidebarOverride } from 'src/app/dashboardv2/layout/DashboardLayout'
import ConfigurationApiContext from 'src/provider/API/v2/call-service/configuration'
import userProfileBg from 'src/assets/v2/img/user_profile_bg.svg'

const UserProfile = () => {
    const navigate = useNavigate()
    const [modalOpen, setModalOpen] = useState(false)

    const { adminProfile, getAdminProfile, changePassword } = useContext(ConfigurationApiContext)

    useEffect(() => {
        getAdminProfile()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleChangePassword = async (value: ChangePasswordValue) => {
        // confirmPassword is frontend-only confirmation — never sent.
        return changePassword({
            currentPassword: value.oldPassword,
            newPassword: value.newPassword,
        })
    }

    const field = (label: string, value: string) => (
        <Flex direction="column" gap="0.4" items="flex-start">
            <Text fontSize="1.3" color={tokens.textLabel} style={fontLabel}>{label}</Text>
            <Text fontSize="1.4" style={fontBold} color={tokens.textValue}>{value}</Text>
        </Flex>
    )

    const initials = adminProfile?.fullName
        ?.split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part: string) => part[0]?.toUpperCase())
        .join('') || undefined

    const PLATFORM_ROLES = ['SA', 'QoreUser', 'QoreDeveloper']
    const sidebarVariant = adminProfile?.role && PLATFORM_ROLES.includes(adminProfile.role) ? 1 : null
    useSidebarOverride({ variant: sidebarVariant })

    return (
        <>
                <div style={{ padding: '2.4rem 3.2rem' }}>
                    <Text fontSize="2.4" fontWeight="700" color={tokens.textDark}>
                        Profile
                    </Text>

                    <Box topMargin="2.4">
                        <div style={{ position: 'relative', overflow: 'hidden', borderRadius: '0.7rem' }}>
                            <Rectangle height="13" bgColor="#0D968F">
                                <Box
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        backgroundImage: `url(${userProfileBg})`,
                                        backgroundRepeat: 'no-repeat',
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                    }}
                                />
                            </Rectangle>
                        </div>

                        <Flex
                            items="flex-end"
                            gap="1.6"
                            style={{ marginTop: '-6.4rem', position: 'relative', paddingLeft: '2.4rem', paddingRight: '2.4rem' }}
                        >
                            <div style={{ flexShrink: 0 }}>
                                <div
                                    style={{
                                        width: '17.5rem',
                                        height: '17.5rem',
                                        borderRadius: '1.2rem',
                                        background: tokens.white,
                                        border: `1px solid ${tokens.borderSoft ?? '#F7F8F9'}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <div
                                        style={{
                                            width: '10.5rem',
                                            height: '10.5rem',
                                            borderRadius: '50%',
                                            overflow: 'hidden',
                                            border: `4px solid ${tokens.white}`,
                                            boxShadow: '0px 12px 16px -4px #10182814, 0px 4px 6px -2px #10182808',
                                        }}
                                    >
                                        <Avatar
                                            size="10.5"
                                            initials={initials}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div style={{ flex: 1 }}>
                                <Card padding="2" radius="0.7" shadow="none" borderColor="transparent">
                                    <Flex direction="column" gap="4.8" items="stretch">
                                        <Flex wrap="wrap" gap="6.4" items="flex-start">
                                            {field('Full name', adminProfile?.fullName ?? '—')}
                                            {field('Email', adminProfile?.email ?? '—')}
                                            {field('Username', adminProfile?.username ?? '—')}
                                            {field('Phone number', adminProfile?.phoneNumber ?? '—')}
                                            <Flex direction="column" gap="0.4" items="flex-start">
                                                <Text fontSize="1.3" color={tokens.textLabel} style={fontLabel}>Status</Text>
                                                <Badge
                                                    bgColor={adminProfile?.isActive === false ? '#FEE4E2' : tokens.successSoft}
                                                    color={adminProfile?.isActive === false ? '#B42318' : tokens.successText}
                                                    radius="999"
                                                    paddingX="1.2"
                                                    paddingY="0.4"
                                                    fontWeight="500"
                                                    className="badge--status"
                                                >
                                                    {adminProfile?.isActive === false ? 'Inactive' : 'Active'}
                                                </Badge>
                                            </Flex>
                                        </Flex>

                                        <Flex justify="flex-end">
                                            <Button variant="primary" size="md" onClick={() => setModalOpen(true)} fontFamily="'Gilmer Regular', sans-serif">
                                                Change Password
                                            </Button>
                                        </Flex>
                                    </Flex>
                                </Card>
                            </div>
                        </Flex>
                    </Box>
                </div>

            <ChangePasswordModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSubmit={handleChangePassword}
            />
        </>
    )
}

export default utility.routeData('profile', 'Profile', <UserProfile />)